import React from 'react';
import { WizardStep } from '@app/app/_components/Wizard/Wizard';
import { CustomerLoginStep } from '@app/app/esales/_sections/steps/CustomerLoginStep';
import { PaymentMethodStep } from '@app/app/esales/_sections/steps/PaymentMethodStep';
import { PaymentMethodTypeStep } from '@app/app/esales/transport/_sections/steps/PaymentMethodTypeStep';
import { PaymentOptionsStep } from '@app/app/esales/transport/_sections/steps/PaymentOptionsStep';
import { VehicleInformationStep } from '@app/app/esales/transport/_sections/steps/VehicleInformationStep/VehicleInformationStep';
import { VehicleOwnerStep } from '@app/app/esales/transport/_sections/steps/VehicleOwnerStep';
import { ChooseInsuranceStep } from '@app/app/esales/transport/_sections/steps/ChooseInsuranceStep';
import { InsuranceTermsStep } from '@app/app/esales/transport/_sections/steps/InsuranceTermsStep';
import { VehicleModelStep } from '@app/app/esales/transport/_sections/steps/VehicleModelStep';
import { AdditionalRisksStep } from '@app/app/esales/transport/_sections/steps/AdditionalRisksStep/AdditionalRisksStep';
import { InsuranceStartDateStep } from '@app/app/esales/transport/_sections/steps/InsuranceStartDateStep';
import { ChooseCascoStep } from '@app/app/esales/transport/_sections/steps/ChooseCascoStep';
import { TransportSummaryStep } from '@app/app/esales/transport/_sections/steps/TransportSummaryStep/TransportSummaryStep';
import { Card } from '@app/app/_components/Card/Card';
import { Client } from '@app/domain/model/Client';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { ProductDataTerritories } from '@app/domain/model/eshop/transport/ProductDataTerritories';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { useOwnerFormFields } from '@app/app/esales/transport/_sections/setups/useOwnerFormFields';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { PlatformSettings } from '@app/domain/model/PlatformSettings';

interface Props {
    t: (key: string) => string;
    requestResult: TransportInsuranceRequestResult;
    formState: FormState<PurchaseTransportInsuranceResult>;
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => Promise<void>;
    discountCodeData: DiscountCodeData;
    mtplPurposes: Nullable<ProductDataPurpose>;
    setInsuranceDuration: React.Dispatch<React.SetStateAction<string>>;
    isCascoPresent: boolean;
    isCompany: boolean;
    personalCodeData: { isValid: boolean; year?: number };
    cascoLeasings: ProductDataLeasing[];
    mtplTerritories: Nullable<ProductDataTerritories>;
    handleInsuranceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    insurancePackage: PremiumType;
    currentPremiums: TransportPremiumsResponse[];
    setCurrentPremiums: (premiums: TransportPremiumsResponse[]) => void;
    handlePremiumOptionsChange: (available: boolean) => void;
    handleCascoPremiumChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    priceState: {
        selectedCascoPremium: Nullable<TransportPremiumsResponse>;
        selectedMtplPremium: Nullable<TransportPremiumsResponse>;
        availableRiders: ProductDataRider[];
        selectedRiders: ProductDataRider[];
        totalPrice: number;
        priceToPayNow: number;
        isLoadingRiders: boolean;
    };
    handleRidersCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    cascoStartDate: string;
    mtplStartDate: string;
    setCascoStartDate: (date: string) => void;
    setMtplStartDate: (date: string) => void;
    authState: AuthState;
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    clients: Client[];
    onSuccessfulLogin: () => void;
    ownerFormFields: ReturnType<typeof useOwnerFormFields>;
    selectedPurpose: { code: string; name: string } | null;
    ownerAge: { code: string; name: string } | null;
    experienceOtherDrivers: { code: string; name: string } | null;
    territory: { code: string; name: string } | null;
    navigateToStep: (stepKey: string) => void;
    handlePaymentFrequencyChange: (frequency: number) => Promise<void>;
    handlePaymentMethodTypeChange: (paymentMethodType: Nullable<string>) => void;
    selectedPaymentMethodType: Nullable<string>;
    isReinitializedSession: boolean;
    initialStep?: Nullable<string>;
    settings: PlatformSettings;
}

export function buildSteps({
    t,
    requestResult,
    formState,
    handleDiscountCode,
    handleApplyDiscountClick,
    discountCodeData,
    mtplPurposes,
    setInsuranceDuration,
    isCascoPresent,
    isCompany,
    personalCodeData,
    cascoLeasings,
    mtplTerritories,
    handleInsuranceChange,
    insurancePackage,
    currentPremiums,
    setCurrentPremiums,
    handlePremiumOptionsChange,
    handleCascoPremiumChange,
    priceState,
    handleRidersCheckboxChange,
    cascoStartDate,
    mtplStartDate,
    setCascoStartDate,
    setMtplStartDate,
    authState,
    swedbankLogoSrc,
    sebLogoSrc,
    clients,
    onSuccessfulLogin,
    ownerFormFields,
    selectedPurpose,
    ownerAge,
    experienceOtherDrivers,
    territory,
    navigateToStep,
    handlePaymentFrequencyChange,
    handlePaymentMethodTypeChange,
    selectedPaymentMethodType,
    isReinitializedSession,
    initialStep,
    settings,
}: Props): WizardStep[] {
    return [
        {
            label: t('eshop.transport.vehicleStepTitle'),
            subSteps: [
                {
                    key: 'step-1-1',
                    when: () => requestResult.showVehicleModelStep,
                    title: t('eshop.transport.vehicleInformation'),
                    body: (
                        <>
                            <input
                                type="hidden"
                                name="licencePlate"
                                value={requestResult.vehicleInformation.licencePlate}
                            />
                            <input
                                type="hidden"
                                name="manufactureYear"
                                value={requestResult.vehicleInformation.manufactureYear?.toString()}
                            />
                            <input type="hidden" name="code" value={requestResult.personalCode} />
                            <VehicleModelStep
                                vehicleInformation={requestResult.vehicleInformation}
                                vehicleModels={requestResult.vehicleModels}
                                formState={formState}
                            />
                        </>
                    ),
                },
                {
                    key: 'step-1-2',
                    title: t('eshop.transport.insuranceTerms'),
                    body: (
                        <InsuranceTermsStep
                            vehicleInformation={requestResult.vehicleInformation}
                            formState={formState}
                            handleDiscountCode={handleDiscountCode}
                            handleApplyDiscountClick={handleApplyDiscountClick}
                            discountCodeData={discountCodeData}
                            mtplPurposes={mtplPurposes}
                            setInsuranceDuration={setInsuranceDuration}
                        />
                    ),
                },
                {
                    key: 'step-1-3',
                    title: t('eshop.transport.vehicleInformation'),
                    body: (
                        <VehicleInformationStep
                            isCascoPresent={isCascoPresent}
                            isCompany={isCompany}
                            criterions={requestResult.cascoCriterions ?? []}
                            ownerYear={personalCodeData.isValid ? personalCodeData.year : undefined}
                            vehicleInformation={requestResult.vehicleInformation}
                            formState={formState}
                            handleDiscountCode={handleDiscountCode}
                            handleApplyDiscountClick={handleApplyDiscountClick}
                            discountCodeData={discountCodeData}
                            cascoLeasings={cascoLeasings}
                            mtplTerritories={mtplTerritories}
                        />
                    ),
                },
            ],
        },
        {
            label: t('eshop.transport.policyStepTitle'),
            subSteps: [
                {
                    key: 'step-2-1',
                    title: t('eshop.transport.chooseInsurance'),
                    body: (
                        <ChooseInsuranceStep
                            onChange={handleInsuranceChange}
                            value={insurancePackage}
                            insuranceDuration={formState.data?.formData.insuranceDuration ?? ''}
                            premiums={currentPremiums}
                            setPremiums={setCurrentPremiums}
                            policyValidity={requestResult.policyValidity ?? null}
                            onPremiumOptionsChange={handlePremiumOptionsChange}
                            isCascoPresent={isCascoPresent}
                        />
                    ),
                },
                {
                    key: 'step-2-2',
                    title: t('eshop.transport.chooseCasco'),
                    when: () => insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl,
                    body: (
                        <ChooseCascoStep
                            onChange={handleCascoPremiumChange}
                            value={priceState.selectedCascoPremium?.code}
                            insuranceDuration={formState.data?.formData.insuranceDuration ?? ''}
                            premiums={currentPremiums}
                            setPremiums={setCurrentPremiums}
                            policyValidity={requestResult.policyValidity ?? null}
                            isCascoPresent={isCascoPresent}
                        />
                    ),
                },
                {
                    key: 'step-2-3',
                    title: t('eshop.transport.additionalRisks'),
                    when: () => priceState.availableRiders.length > 0,
                    body: (
                        <AdditionalRisksStep
                            handleRidersCheckboxChange={handleRidersCheckboxChange}
                            availableRiders={priceState.availableRiders}
                            selectedRiders={priceState.selectedRiders}
                        />
                    ),
                },
                {
                    key: 'step-2-4',
                    title: t('eshop.transport.insuranceStartDates'),
                    body: (
                        <InsuranceStartDateStep
                            insuranceDuration={formState.data?.formData.insuranceDuration}
                            policyValidity={requestResult.policyValidity ?? null}
                            insurancePackage={insurancePackage}
                            setCascoStartDate={setCascoStartDate}
                            setMtplStartDate={setMtplStartDate}
                            cascoStartDate={cascoStartDate}
                            mtplStartDate={mtplStartDate}
                        />
                    ),
                },
                {
                    key: 'step-2-5',
                    when: () =>
                        settings.general.esalesTransportAuth &&
                        (!authState.authenticated ||
                            !authState.clientSelected ||
                            (isReinitializedSession && initialStep === 'step-2-5')),
                    title: t('eshop.general.ifYouAlreadyClient'),
                    body: (
                        <CustomerLoginStep
                            swedbankLogoSrc={swedbankLogoSrc}
                            sebLogoSrc={sebLogoSrc}
                            clients={clients}
                            authState={authState}
                            onContinue={onSuccessfulLogin}
                            onSuccess={onSuccessfulLogin}
                            bankAuthEnabled={settings.general.esalesTransportBankAuth}
                        />
                    ),
                },
                {
                    key: 'step-2-6',
                    title: t('eshop.transport.vehicleOwnerInformation'),
                    body: (
                        <Card className="gap-8 p-6">
                            <VehicleOwnerStep
                                ownerFormFields={ownerFormFields}
                                formState={formState}
                                isCompany={isCompany}
                            />
                        </Card>
                    ),
                },
                {
                    key: 'step-2-7',
                    title: t('eshop.transport.summary'),
                    body: (
                        <TransportSummaryStep
                            isCompany={isCompany}
                            vehicleInformation={requestResult.vehicleInformation}
                            formState={formState}
                            selectedPurpose={selectedPurpose ?? null}
                            ownerAge={ownerAge}
                            experienceOtherDrivers={experienceOtherDrivers}
                            territory={territory ?? null}
                            ownerFormFields={ownerFormFields}
                            totalPrice={priceState.totalPrice}
                            priceToPayNow={priceState.priceToPayNow}
                            selectedRiders={priceState.selectedRiders}
                            insurancePackage={insurancePackage}
                            onNavigateToStep={navigateToStep}
                            requestResult={requestResult}
                            setCascoStartDate={setCascoStartDate}
                            setMtplStartDate={setMtplStartDate}
                            cascoStartDate={cascoStartDate}
                            mtplStartDate={mtplStartDate}
                            onPaymentFrequencyChange={handlePaymentFrequencyChange}
                            cascoPackageName={priceState.selectedCascoPremium?.title ?? ''}
                        />
                    ),
                },
            ],
        },
        {
            label: t('eshop.transport.paymentStepTitle'),
            subSteps: [
                {
                    key: 'step-3-1',
                    title: (
                        <div className="flex w-full flex-row items-center justify-between gap-4">
                            {t('eshop.transport.choosePaymentOptions')}
                            <InformationPopover content={t('eshop.transport.paymentOptionsTooltip')}>
                                <QuestionCircleIcon className="size-5 text-neutral-500" />
                            </InformationPopover>
                        </div>
                    ),
                    body: (
                        <PaymentOptionsStep
                            selectedCascoPremium={priceState.selectedCascoPremium}
                            selectedMtplPremium={priceState.selectedMtplPremium}
                            selectedRiders={priceState.selectedRiders}
                            formState={formState}
                        />
                    ),
                },
                {
                    key: 'step-3-2',
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodTypeStep formState={formState} onChange={handlePaymentMethodTypeChange} />,
                },
                {
                    key: 'step-3-3',
                    when: () => selectedPaymentMethodType === PaymentMethodType.Banks,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodStep paymentMethods={formState.data?.paymentMethods ?? []} />,
                },
            ],
        },
    ];
}
